// CreateStepDescription — wizard pane 6 (description + publish block).
// Substrate: design/add.html lines 646-685 — `<section data-pane="6">`.
//
// Fields (PropertyWriteSerializer): description_public, description_private.
// Plain controlled textareas (CLAUDE.md: textarea is permanently
// plain-text only — no autosize/markdown/toolbar).
//
// The `.ap-final` block carries the two submit actions (draft / publish),
// matching add.html. The composite POST → location → subtype → features →
// publish flow is owned by CreateRealtyScreenContent; the buttons stay
// type="submit" (design fidelity) but invoke onSaveDraft / onPublish via
// onClick, while the parent form's onSubmit preventDefaults the native
// reload (so Enter in a field never triggers an accidental submit).

function CreateStepDescription({
  active,
  values,
  onChange,
  fieldErrors = {},
  onSaveDraft,
  onPublish,
  submitting = false,
  submitError = null,
  /** "personal" (default) keeps catalog-publish copy; "corporate" = work activation only. */
  finishMode = "personal",
  finalTitle,
  finalBody,
  finalHint,
  draftActionLabel,
  publishActionLabel,
  publishActionIcon,
}) {
  const setField = (key, value) => onChange({ ...values, [key]: value });
  const isCorporate = finishMode === "corporate";
  const resolvedTitle =
    finalTitle ||
    (isCorporate ? "Готово до робочої активації?" : "Готово до публікації?");
  const resolvedBody =
    finalBody ||
    (isCorporate ? (
      <>
        За замовчуванням об'єкт зберігається як{" "}
        <strong>чернетка</strong> (робочий статус DRAFT). «Активувати для
        роботи» переводить робочий статус у <strong>ACTIVE</strong>. Це{" "}
        <strong>не</strong> публікація в публічному каталозі — каталог
        публікується окремо на сторінці об'єкта.
      </>
    ) : (
      <>
        За замовчуванням оголошення зберігається як{" "}
        <strong>чернетка</strong> (DRAFT). Опублікуйте — і воно з'явиться в
        пошуку зі статусом <strong>ACTIVE</strong>.
      </>
    ));
  const resolvedHint =
    finalHint ||
    (isCorporate
      ? "Для активації потрібно: тип, локація (країна+область), характеристики (за типом)."
      : "Для активації потрібно: тип, локація (країна+область), характеристики (за типом).");
  const resolvedDraftLabel = draftActionLabel || "Зберегти як чернетку";
  const resolvedPublishLabel =
    publishActionLabel ||
    (isCorporate ? "Активувати для роботи" : "Опублікувати");
  const resolvedPublishIcon =
    publishActionIcon ||
    (isCorporate ? "fa-solid fa-play" : "fa-solid fa-rocket");

  return (
    <section
      className={active ? "ap-pane is-active" : "ap-pane"}
      data-pane="6"
      aria-labelledby="ap-pane-6"
      hidden={!active}
    >
      <header className="ap-pane__head">
        <h2 id="ap-pane-6">
          <i className="fa-solid fa-pen" aria-hidden="true" />
          Опис
        </h2>
        <p>
          Публічний опис побачать усі (включно з анонімами). Приватні нотатки —
          лише авторизовані рієлтори.
        </p>
      </header>

      <div className="ap-row">
        <div className="ap-field ap-field--wide">
          <label htmlFor="desc-public">
            Публічний опис{" "}
            <i
              className="fa-solid fa-eye visibility-icon"
              title="Видно всім"
              aria-hidden="true"
            />
          </label>
          <textarea
            id="desc-public"
            name="description_public"
            className="form-control"
            rows={6}
            placeholder="Розкажіть про переваги об'єкта, інфраструктуру, особливості…"
            value={values.description_public || ""}
            onChange={(e) => setField("description_public", e.target.value)}
          />
          {fieldErrors.description_public ? (
            <small className="form-hint text-danger">
              {fieldErrors.description_public}
            </small>
          ) : null}
        </div>
      </div>

      <div className="ap-row">
        <div className="ap-field ap-field--wide">
          <label htmlFor="desc-private">
            Приватні нотатки{" "}
            <i
              className="fa-solid fa-lock visibility-icon"
              title="Лише авторизовані рієлтори"
              aria-hidden="true"
            />
          </label>
          <textarea
            id="desc-private"
            name="description_private"
            className="form-control"
            rows={4}
            placeholder="МЛС, поведінка власника, особливості зв'язку — лише для колег-рієлторів."
            value={values.description_private || ""}
            onChange={(e) => setField("description_private", e.target.value)}
          />
          {fieldErrors.description_private ? (
            <small className="form-hint text-danger">
              {fieldErrors.description_private}
            </small>
          ) : null}
        </div>
      </div>

      <div className="ap-final">
        <h3>{resolvedTitle}</h3>
        <p>{resolvedBody}</p>
        <p className="ap-final__hint">
          <i className="fa-solid fa-circle-info" aria-hidden="true" />
          {resolvedHint}
        </p>
        {submitError ? (
          <p className="ap-helper text-danger" role="alert">
            {submitError}
          </p>
        ) : null}
        <div className="ap-final__actions">
          <button
            type="submit"
            name="action"
            value="draft"
            className="action-btn"
            onClick={onSaveDraft}
            disabled={submitting}
          >
            <i className="fa-solid fa-floppy-disk" aria-hidden="true" />
            {resolvedDraftLabel}
          </button>
          <button
            type="submit"
            name="action"
            value={isCorporate ? "activate" : "publish"}
            className="btn-more"
            onClick={onPublish}
            disabled={submitting}
          >
            <i className={resolvedPublishIcon} aria-hidden="true" />
            {resolvedPublishLabel}
          </button>
        </div>
      </div>
    </section>
  );
}

export default CreateStepDescription;
